import React, { useState, useEffect } from "react";
import {
  Table,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Tablecell,
  findStrategy,
  findFundAbbById,
} from "../../../../../component/Methods";
import { useSelector } from "react-redux";
import { selectFunds, selectStrategies, selectRebalancerSummary, selectActiveRequests } from "../../../../../Redux/Reducers/selectors";
import SummaryTableSkeleton from "../../../Skeletons/SummaryTableSkeleton";
import { blueGrey } from "@mui/material/colors";

const formatNumber = (value) => {
  const num = parseInt(value, 10);
  if (isNaN(num)) return value;
  const absNum = Math.abs(num);
  const formattedNumber = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(absNum);

  return num < 0 ? `(${formattedNumber})` : `${formattedNumber}\u00A0`;
};

/** Numeric fields on each strategyTotals row; all zero ⇒ hide strategy in dropdown / expand rows. */
const STRATEGY_TOTAL_ACTIVITY_FIELDS = [
  "tradeCount",
  "shares",
  "dollars",
  "marketTradeCount",
  "crossTradeCount",
  "marketShares",
  "crossShares",
  "marketDollars",
  "crossDollars",
];

function strategyTotalsRowHasActivity(row) {
  if (!row || typeof row !== "object") return false;
  return STRATEGY_TOTAL_ACTIVITY_FIELDS.some((field) => {
    const raw = row[field];
    if (raw == null) return false;
    const n = typeof raw === "number" ? raw : Number(raw);
    return !Number.isNaN(n) && n !== 0;
  });
}

function strategyTotalsRowsHaveActivity(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.some(strategyTotalsRowHasActivity);
}

function pickStrategyTotalsWithActivity(strategyTotals) {
  if (!strategyTotals || typeof strategyTotals !== "object") return {};
  return Object.fromEntries(
    Object.entries(strategyTotals).filter(([, rows]) =>
      strategyTotalsRowsHaveActivity(rows)
    )
  );
}

/** Summary rows always appear in this order (not column-sort). */
const SUMMARY_TRADE_TYPE_ORDER = [
  "Buy Long",
  "Sell Long",
  "Sell Short",
  "Cover Short",
];

function sortDataRowsByTradeTypeOrder(rows) {
  if (!Array.isArray(rows)) return [];
  const rank = (t) => {
    const i = SUMMARY_TRADE_TYPE_ORDER.indexOf(t);
    return i === -1 ? SUMMARY_TRADE_TYPE_ORDER.length : i;
  };
  return [...rows].sort((a, b) => rank(a?.type) - rank(b?.type));
}

const SummaryTable = ({ tableHeader }) => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [strategyData, setStrategyData] = useState({});
  const [selectedFundId, setSelectedFundId] = useState("All");
  const [selectedStrategyId, setSelectedStrategyId] = useState("All");
  const funds = useSelector(selectFunds);
  const rebalancerSummary = useSelector(selectRebalancerSummary);
 const activeRequests = useSelector(selectActiveRequests);
  const strategies = useSelector(selectStrategies);

  const handleRowClick = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const handleFundIdChange = (event) => {
    setSelectedFundId(event.target.value);
    setSelectedStrategyId("All");
    setExpandedRow(null);
  };

  const handleStrategyIdChange = (event) => {
    setSelectedStrategyId(event.target.value);
    setExpandedRow(null);
  };

  useEffect(() => {
    if (!selectedFundId && Object.keys(rebalancerSummary)?.length > 0) {
      const firstFundId = Object.keys(rebalancerSummary)[0];
      setSelectedFundId(firstFundId);
    }
  }, [rebalancerSummary, selectedFundId]);

  useEffect(() => {
    setSelectedStrategyId("All");
    setExpandedRow(null);
  }, [selectedFundId]);

  useEffect(() => {
    if (selectedFundId === "All") {
      const aggregatedData = Object.values(rebalancerSummary)?.reduce(
        (acc, fundData) => {
          const rowsForFund = sortDataRowsByTradeTypeOrder(
            fundData?.totals || []
          );

          rowsForFund?.forEach((row) => {
            if (!acc[row.type]) {
              acc[row.type] = {
                ...row,
                tradeCount: 0,
                shares: 0,
                dollars: 0,
                marketTrades: 0,
                crossingTrades: 0,
                marketShares: 0,
                crossingShares: 0,
                marketDollars: 0,
                crossingDollars: 0
              };
            }

            acc[row.type].tradeCount += row.tradeCount;
            acc[row.type].shares += row.shares;
            acc[row.type].dollars += parseInt(row.dollars, 10);
            acc[row.type].marketTrades += row.marketTradeCount || 0;
            acc[row.type].crossingTrades += row.crossTradeCount || 0;
            acc[row.type].marketShares += row.marketShares || 0;
            acc[row.type].crossingShares += row.crossShares || 0;
            acc[row.type].marketDollars += parseInt(row.marketDollars || 0, 10);
            acc[row.type].crossingDollars += parseInt(row.crossDollars || 0, 10);
          });

          return acc;
        },
        {}
      );

      const aggregatedRows = Object.keys(aggregatedData)?.map((type) => {
        const row = aggregatedData[type];
        return {
          type,
          tradeCount: row?.tradeCount?.toString() || "0",
          shares: row?.shares?.toString() || "0",
          dollars: row?.dollars?.toString() || "0",
          marketTrades: row?.marketTrades?.toString() || "0",
          crossingTrades: row?.crossingTrades?.toString() || "0",
          marketShares: row?.marketShares?.toString() || "0",
          crossingShares: row?.crossingShares?.toString() || "0",
          marketDollars: row?.marketDollars?.toString() || "0",
          crossingDollars: row?.crossingDollars?.toString() || "0",
        };
      });

      const overallTotals = aggregatedRows?.reduce(
        (acc, row) => {
          acc.tradeCount += Math.abs(row?.tradeCount);
          acc.shares += Math.abs(row?.shares);
          acc.dollars += parseInt(row?.dollars, 10);
          acc.marketTrades += Math.abs(row?.marketTrades);
          acc.crossingTrades += Math.abs(row?.crossingTrades);
          acc.marketShares += Math.abs(row?.marketShares);
          acc.crossingShares += Math.abs(row?.crossingShares);
          acc.marketDollars += parseInt(row?.marketDollars, 10);
          acc.crossingDollars += parseInt(row?.crossingDollars, 10);
          return acc;
        },
        {
          tradeCount: 0,
          shares: 0,
          dollars: 0,
          marketTrades: 0,
          crossingTrades: 0,
          marketShares: 0,
          crossingShares: 0,
          marketDollars: 0,
          crossingDollars: 0
        }
      );

      const totalRow = {
        type: "Total",
        tradeCount: overallTotals?.tradeCount?.toString() || "0",
        shares: overallTotals?.shares?.toString() || "0",
        dollars: overallTotals?.dollars?.toString() || "0",
        marketTrades: overallTotals?.marketTrades?.toString() || "0",
        crossingTrades: overallTotals?.crossingTrades?.toString() || "0",
        marketShares: overallTotals?.marketShares?.toString() || "0",
        crossingShares: overallTotals?.crossingShares?.toString() || "0",
        marketDollars: overallTotals?.marketDollars?.toString() || "0",
        crossingDollars: overallTotals?.crossingDollars?.toString() || "0",
      };

      setData([...aggregatedRows, totalRow]);
      setStrategyData({});
    } else if (selectedFundId && rebalancerSummary[selectedFundId]) {
      const rawStrategyTotals =
        rebalancerSummary[selectedFundId]?.strategyTotals || {};
      const strategyTotalsWithActivity =
        pickStrategyTotalsWithActivity(rawStrategyTotals);

      const strategyIdForTable =
        selectedStrategyId !== "All" &&
        Object.prototype.hasOwnProperty.call(
          strategyTotalsWithActivity,
          selectedStrategyId
        )
          ? selectedStrategyId
          : "All";

      let dataToSort = rebalancerSummary[selectedFundId]?.totals || [];

      if (strategyIdForTable !== "All") {
        dataToSort =
          strategyTotalsWithActivity[strategyIdForTable] || [];
      }

      const sortedData = sortDataRowsByTradeTypeOrder(dataToSort);

      // Transform backend field names to frontend field names
      const transformedData = sortedData?.map((row) => ({
        type: row.type,
        tradeCount: row.tradeCount?.toString() || "0",
        shares: row.shares?.toString() || "0",
        dollars: row.dollars?.toString() || "0",
        marketTrades: row.marketTradeCount?.toString() || "0",
        crossingTrades: row.crossTradeCount?.toString() || "0",
        marketShares: row.marketShares?.toString() || "0",
        crossingShares: row.crossShares?.toString() || "0",
        marketDollars: row.marketDollars?.toString() || "0",
        crossingDollars: row.crossDollars?.toString() || "0",
      }));

      const totals = transformedData?.reduce(
        (acc, row) => {
          acc.tradeCount += Math.abs(row?.tradeCount);
          acc.shares += Math.abs(row?.shares);
          acc.dollars += parseInt(row?.dollars, 10);
          acc.marketTrades += Math.abs(row?.marketTrades || 0);
          acc.crossingTrades += Math.abs(row?.crossingTrades || 0);
          acc.marketShares += Math.abs(row?.marketShares || 0);
          acc.crossingShares += Math.abs(row?.crossingShares || 0);
          acc.marketDollars += parseInt(row?.marketDollars || 0, 10);
          acc.crossingDollars += parseInt(row?.crossingDollars || 0, 10);
          return acc;
        },
        {
          tradeCount: 0,
          shares: 0,
          dollars: 0,
          marketTrades: 0,
          crossingTrades: 0,
          marketShares: 0,
          crossingShares: 0,
          marketDollars: 0,
          crossingDollars: 0
        }
      );

      const totalRow = {
        type: "Total",
        tradeCount: totals?.tradeCount?.toString() || "0",
        shares: totals?.shares?.toString() || "0",
        dollars: totals?.dollars?.toString() || "0",
        marketTrades: totals?.marketTrades?.toString() || "0",
        crossingTrades: totals?.crossingTrades?.toString() || "0",
        marketShares: totals?.marketShares?.toString() || "0",
        crossingShares: totals?.crossingShares?.toString() || "0",
        marketDollars: totals?.marketDollars?.toString() || "0",
        crossingDollars: totals?.crossingDollars?.toString() || "0",
      };

      setData([...transformedData, totalRow]);

      setStrategyData(strategyTotalsWithActivity);

      if (
        selectedStrategyId !== "All" &&
        strategyIdForTable === "All"
      ) {
        setSelectedStrategyId("All");
      }
    }
  }, [selectedFundId, selectedStrategyId, rebalancerSummary]);

  // Check if we're loading summary data
  const isLoadingSummary = activeRequests.includes("rebalancer_summary");

  // Show skeleton while loading
  if (isLoadingSummary) {
    return <SummaryTableSkeleton />;
  }

  const canExpandStrategyBreakdown =
    selectedFundId !== "All" && selectedStrategyId === "All";

  return (
    <Box
      sx={{
        borderRadius: "4px",
        width: "100%",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "visible",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          overflow: "visible",
          position: "relative",
          zIndex: 1,
          pb: 1,
        }}
      >
      <Box sx={{ minHeight: 24, marginBottom: 1 }} aria-hidden />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 72 }}>
                <Select
                  value={selectedFundId}
                  onChange={handleFundIdChange}
                  sx={{ fontSize: "12px", height: 32 }}
                >
                  <MenuItem value="All" sx={{ fontSize: "12px" }}>
                    All
                  </MenuItem>
                  {Object.keys(rebalancerSummary).map((fundId) => (
                    <MenuItem key={fundId} value={fundId} sx={{ fontSize: "12px" }}>
                      {findFundAbbById(fundId, funds)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={selectedStrategyId}
                  onChange={handleStrategyIdChange}
                  disabled={selectedFundId === "All"}
                  sx={{ fontSize: "12px", height: 32 }}
                >
                  <MenuItem value="All" sx={{ fontSize: "12px" }}>
                    All Strategies
                  </MenuItem>
                  {selectedFundId !== "All" &&
                    Object.keys(strategyData)
                      ?.sort((keyA, keyB) =>
                        findStrategy(keyA, strategies)?.localeCompare(
                          findStrategy(keyB, strategies)
                        )
                      )
                      ?.map((strategyId) => (
                        <MenuItem key={strategyId} value={strategyId} sx={{ fontSize: "12px" }}>
                          {findStrategy(strategyId, strategies)}
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
      </Box>

      <Box
        sx={{
          flex: "1 1 0px",
          minHeight: 0,
          height: 0,
          maxHeight: "100%",
          overflow: "auto",
          display: "block",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "4px",
          "&::-webkit-scrollbar": {
            width: "5px",
            height: "3px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f0f0f0",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c0c0c0",
            borderRadius: "10px",
            minHeight: "2px",
          },
        }}
      >
        <Table
          size="small"
          stickyHeader
          aria-label="a dense table"
          sx={{
            borderCollapse: "collapse",
            border: "solid 1px #eee",
            marginTop: "-1px",
          }}
        >
          <thead>
            <TableRow>
              <TableCell
                colSpan={10}
              align="center"
              style={{
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[500],
                color: "white",
                fontSize: "12px",
                fontWeight: "bold",
                padding: "8px 0",
                color: theme.palette.text.primary,
              }}
            >
              SUMMARY
              </TableCell>
            </TableRow>
            {/* First header row - merged categories */}
            <TableRow sx={{ background: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100] }}>
              <TableCell
                align="center"
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "4px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  backgroundColor: theme.palette.lightGrey.tableHeader,
                  color:  theme.palette.text.primary,
                }}
              >
                Type
              </TableCell>
              <TableCell
                colSpan={3}
                align="center"
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "4px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  backgroundColor: theme.palette.lightGrey.tableHeader,
                  color:  theme.palette.text.primary,
                }}
              >
                Trade Count
              </TableCell>
              <TableCell
                colSpan={3}
                align="center"
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "4px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  backgroundColor: theme.palette.lightGrey.tableHeader,
                  color:  theme.palette.text.primary,
                }}
              >
                Shares
              </TableCell>
              <TableCell
                colSpan={3}
                align="center"
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "4px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  backgroundColor: theme.palette.lightGrey.tableHeader,
                  color:  theme.palette.text.primary,
                }}
              >
                Dollars
              </TableCell>
            </TableRow>
            {/* Second header row - individual column headers (fixed row order; columns not sortable) */}
            <TableRow sx={{ background: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200] }}>
              <TableCell
                align="center"
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                }}
              >
              </TableCell>
              <TableCell
                align="center"
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Market
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Crossing
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Total
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Market
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Crossing
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Total
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Market
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Crossing
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Total
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          </thead>

          <TableBody>
            {data?.map((row, index) => (
              <React.Fragment key={index}>
                <TableRow
                  onClick={() => {
                    if (
                      canExpandStrategyBreakdown &&
                      row.type !== "Total"
                    ) {
                      handleRowClick(index);
                    }
                  }}
                  sx={{
                    "& > .MuiTableCell-root": {
                      paddingTop: "1px",
                      paddingBottom: "0px !important",
                      lineHeight: "1.2",
                      "& .MuiInputBase-input": {
                        padding: "0px 0px !important",
                      },
                    },
                    "&:hover":
                      canExpandStrategyBreakdown && row.type !== "Total"
                        ? {
                            background: theme.palette.grey[200],
                            cursor: "pointer",
                          }
                        : {},
                    cursor:
                      canExpandStrategyBreakdown && row.type !== "Total"
                        ? "pointer"
                        : "default",
                    backgroundColor:
                      row.type === "Total"
                        ? theme.palette.grey[200]
                        : "inherit",
                  }}
                >
                  <Tablecell
                    align="left"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: "bold",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {row.type}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.marketTrades)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.crossingTrades)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.tradeCount)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.marketShares)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.crossingShares)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.shares)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.marketDollars)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.crossingDollars)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.dollars)}
                  </Tablecell>
                </TableRow>
                {expandedRow === index &&
                  canExpandStrategyBreakdown &&
                  (() => {
                  return Object.entries(strategyData)
                    ?.sort(([keyA], [keyB]) =>
                      findStrategy(keyA, strategies)?.localeCompare(
                        findStrategy(keyB, strategies)
                      )
                    )?.map(([key, value]) => (
                      <TableRow
                        key={key}
                        sx={{
                          // MUI blueGrey for expanded strategy rows
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? blueGrey[800]
                              : blueGrey[100],
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? blueGrey[700]
                                : blueGrey[200],
                          },
                        }}
                      >
                        <Tablecell
                          style={{
                            paddingLeft: "5px",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                            backgroundColor: "transparent",
                          }}
                        >
                          {findStrategy(key, strategies)}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.marketTradeCount
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.crossTradeCount
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row.type)?.tradeCount
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.marketShares
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.crossShares
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row.type)?.shares
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.marketDollars
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.crossDollars
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.dollars
                          )}
                        </Tablecell>
                      </TableRow>
                    ));
                })()}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default SummaryTable;
